import React, { useContext, useRef, useMemo, useEffect, useCallback } from "react";
import { ReviewsContext } from "../context/ReviewsContext";
import { Card, CardContent, List, ListItem, ListItemText, Box, Typography, Divider, CircularProgress, CardHeader, Button } from "@mui/material";
import { DashboardCardTitle, MODERN_CARD_STYLE, CARD_HEADER_STYLE, CARD_CONTENT_STYLE, StandardChartCard } from './common/ChartDefaults';
import { formatCurrency } from '../utils/currencyUtils';
import { BusinessContext } from "../context/BusinessContext";
import { colors } from '../utils/themeUtils';
import ChatIcon from '@mui/icons-material/Chat';
import { NoMaxWidthTooltip, TooltipContent, CommentText } from './common/SharedTooltip';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const QuoteMark = styled('span')({
  color: 'rgba(0, 0, 0, 0.4)',
  fontWeight: 400,
});

const capitalizeFirstLetter = (string) => {
  if (!string) return "N/A";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Format impact values with proper precision for Rating view
const formatImpactExact = (impact) => {
  if (impact === undefined || impact === null) return '0.000 ★';
  
  const absImpact = Math.abs(impact);
  
  // For zero values
  if (absImpact === 0) return '0.000 ★';
  
  // For normal values (≥ 0.001), use standard 3 decimal places
  if (absImpact >= 0.001) {
    return `${absImpact.toFixed(3)} ★`;
  }
  
  // For very small values, find the first significant digit
  const scientificNotation = absImpact.toExponential();
  const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
  
  if (matches) {
    const firstDigit = matches[1]; // First significant digit
    const exponent = parseInt(matches[3], 10);
    
    // Create the string with leading zeros
    let result = '0.';
    for (let i = 1; i < exponent; i++) {
      result += '0';
    }
    result += firstDigit;
    
    return `${result} ★`;
  }
  
  // Fallback to standard formatting
  return `${absImpact.toFixed(3)} ★`;
};

const CommentsSideBySide = () => {
  const scrollContainerRef = useRef(null);
  const {
    selectedCategory,
    selectedSubject,
    selectedAttribute,
    selectedDate,
    selectedDateRange,
    selectedSources,
    view,
    timeframe,
    positiveCommentsPanelItems,
    negativeCommentsPanelItems,
    isCommentsPanelLoading,
    commentsPanelError,
    commentsPanelPage,
    commentsPanelHasMore,
    fetchCommentsForPanel,
    setSelectedReviewId
  } = useContext(ReviewsContext);

  const { currencySymbol, selectedBusinesses } = useContext(BusinessContext);
  const navigate = useNavigate();

  const isLoading = isCommentsPanelLoading;

  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      const businessIds = selectedBusinesses.map(b => b.id);
      fetchCommentsForPanel({
        pageToFetch: 1,
        businessIds,
        currentTf: timeframe,
        currentSelectedCategory: selectedCategory,
        currentSelectedSubject: selectedSubject,
        currentSelectedAttribute: selectedAttribute,
        currentSelectedDate: selectedDate,
        currentSelectedDateRange: selectedDateRange,
        currentSelectedSources: selectedSources,
        resetData: true 
      });
    } else {
      fetchCommentsForPanel({ businessIds: [], pageToFetch: 1, resetData: true });
    }
  }, [selectedBusinesses, timeframe, selectedCategory, selectedSubject, selectedAttribute, selectedDate, selectedDateRange, selectedSources, fetchCommentsForPanel]);

  const positiveCommentGroups = useMemo(() => {
    if (!positiveCommentsPanelItems) return [];
    return positiveCommentsPanelItems.map(commentItem => ({
      comment: commentItem.comment_text,
      reviewId: commentItem.review_id,
      businessId: commentItem.business_id,
      businessName: commentItem.business_name,
      reviewDate: commentItem.review_date,
      reviewRating: commentItem.review_rating,
      items: (commentItem.analysis_items_for_comment || []).map(ai => ({
          subject_group: ai.subject,
          attribute: ai.attribute,
          attribute_impact: ai.attribute_impact,
          sentiment: ai.sentiment,
          category: ai.category
      }))
    }));
  }, [positiveCommentsPanelItems]);

  const negativeCommentGroups = useMemo(() => {
    if (!negativeCommentsPanelItems) return [];
    return negativeCommentsPanelItems.map(commentItem => ({
      comment: commentItem.comment_text,
      reviewId: commentItem.review_id,
      businessId: commentItem.business_id,
      businessName: commentItem.business_name,
      reviewDate: commentItem.review_date,
      reviewRating: commentItem.review_rating,
      items: (commentItem.analysis_items_for_comment || []).map(ai => ({
          subject_group: ai.subject,
          attribute: ai.attribute,
          attribute_impact: ai.attribute_impact,
          sentiment: ai.sentiment,
          category: ai.category
      }))
    }));
  }, [negativeCommentsPanelItems]);

  const title = useMemo(() => {
    let baseTitle = "Customer Insights";
    if (selectedCategory || selectedSubject || selectedAttribute) {
      baseTitle += " for";
      if (selectedCategory) baseTitle += ` ${capitalizeFirstLetter(selectedCategory)}`;
      if (selectedSubject) baseTitle += `${selectedCategory ? "," : ""} ${capitalizeFirstLetter(selectedSubject)}`;
      if (selectedAttribute) baseTitle += `${(selectedCategory || selectedSubject) ? " " : ""} ${capitalizeFirstLetter(selectedAttribute)}`;
    }
    let dateSuffix = "";
    if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
        const options = { month: 'short', year: 'numeric' };
        const formattedDate = selectedDate.toLocaleDateString(undefined, options);
        dateSuffix = ` (${formattedDate})`;
    } else if (selectedDate && typeof selectedDate === 'string') {
        dateSuffix = ` (${selectedDate})`;
    }
    return (
      <>
        {baseTitle}
        {dateSuffix && <span style={{ color: '#78909C', fontSize: '0.8em', fontWeight: 400 }}>{dateSuffix}</span>}
      </>
    );
  }, [selectedCategory, selectedSubject, selectedAttribute, selectedDate]);

  const handleLoadMore = useCallback(() => {
    if (!isCommentsPanelLoading && commentsPanelHasMore && selectedBusinesses && selectedBusinesses.length > 0) {
      const businessIds = selectedBusinesses.map(b => b.id);
      fetchCommentsForPanel({
        pageToFetch: commentsPanelPage + 1,
        businessIds,
        currentTf: timeframe,
        currentSelectedCategory: selectedCategory,
        currentSelectedSubject: selectedSubject,
        currentSelectedAttribute: selectedAttribute,
        currentSelectedDate: selectedDate,
        currentSelectedDateRange: selectedDateRange,
        currentSelectedSources: selectedSources,
        resetData: false
      });
    }
  }, [isCommentsPanelLoading, commentsPanelHasMore, commentsPanelPage, selectedBusinesses, timeframe, selectedCategory, selectedSubject, selectedAttribute, selectedDate, selectedDateRange, selectedSources, fetchCommentsForPanel]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      if (scrollContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
          if (!isCommentsPanelLoading && commentsPanelHasMore) {
            handleLoadMore();
          }
        }
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleLoadMore, isCommentsPanelLoading, commentsPanelHasMore]);

  const renderCommentGroup = (commentGroup, type) => {
    const { comment, items, reviewId, businessId, businessName, reviewDate, reviewRating } = commentGroup;
    const reviewDateObj = reviewDate ? new Date(reviewDate) : null;
    const formattedReviewDate = reviewDateObj && !isNaN(reviewDateObj.getTime()) 
        ? reviewDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : "Invalid Date";

    let totalRevenueImpactForComment = 0;
    if (view === "Sales") {
        const businessForTurnover = selectedBusinesses?.find(b => b.id === businessId);
        const turnover = businessForTurnover?.turnover;
        if (turnover) {
            (items || []).forEach(item => {
                if (item.attribute_impact) {
                    totalRevenueImpactForComment += item.attribute_impact * turnover * 0.1;
                }
            });
        }
    }

    const formatImpactValueForComment = (item) => {
        if (view === "Sales") {
            const businessForTurnover = selectedBusinesses?.find(b => b.id === businessId);
            const turnover = businessForTurnover?.turnover;
            if (turnover && item.attribute_impact) {
                const revenue = item.attribute_impact * turnover * 0.1;
                return formatCurrency(revenue, currencySymbol, view);
            }
            return formatCurrency(0, currencySymbol, view); 
        }
        return formatImpactExact(item.attribute_impact);
    };

    // Find the business address from selectedBusinesses
    const businessForAddress = selectedBusinesses?.find(b => b.id === businessId);
    const businessAddress = businessForAddress?.address || '';
    
    // Extract first part of address (up to first comma, or first 30 characters)
    const getAddressSnippet = (address) => {
      if (!address) return '';
      const firstCommaIndex = address.indexOf(',');
      if (firstCommaIndex > 0 && firstCommaIndex <= 30) {
        return address.substring(0, firstCommaIndex);
      }
      return address.length > 30 ? address.substring(0, 30) + '...' : address;
    };

    const addressSnippet = getAddressSnippet(businessAddress);
    const businessNameWithAddress = `${businessName}${addressSnippet ? ` - ${addressSnippet}` : ''}`;

    const tooltipTitle = (
      <>
        <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'bold' }}>
          {businessNameWithAddress} ({formattedReviewDate}) - Rating: {reviewRating || "N/A"}★
          {view === "Sales" && totalRevenueImpactForComment !== 0 && (
            ` | Total Comment Revenue: ${formatCurrency(totalRevenueImpactForComment, currencySymbol, view)}`
          )}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <TooltipContent 
          items={items || []}
          formatImpactValue={formatImpactValueForComment}
        />
      </>
    );

    return (
      <ListItem 
        key={`${reviewId}-${comment.substring(0, 20)}-${type}`} 
        sx={{ alignItems: 'flex-start', py: 0.5, px: 1, cursor: 'pointer'}}
        data-filter-exempt="true"
        onClick={() => {
          if (setSelectedReviewId) {
            setSelectedReviewId(reviewId);
          }
          navigate(`/reviews?review_id=${reviewId}`);
        }}
      >
        <NoMaxWidthTooltip title={tooltipTitle} placement="top" arrow>
          <ListItemText sx={{ m: 0 }}>
            <CommentText
              data-filter-exempt="true"
              sx={{ color: type === 'positive' ? colors.positiveGreen : undefined }}
            >
              <QuoteMark>“</QuoteMark>{comment}<QuoteMark>”</QuoteMark>
            </CommentText>
          </ListItemText>
        </NoMaxWidthTooltip>
      </ListItem>
    );
  };

  const noCommentsAtAll = !isLoading && positiveCommentGroups.length === 0 && negativeCommentGroups.length === 0 && !commentsPanelError;

  return (
    <Card 
      sx={MODERN_CARD_STYLE}
      data-chart-type="comments-panel"
      data-filter-exempt="true"
    >
      <Box sx={{ height: '56px' }}>
        <CardHeader 
           sx={{
            ...CARD_HEADER_STYLE,
            justifyContent: 'flex-start',
            '& .MuiCardHeader-content': {
              overflow: 'hidden'
            }
          }}
          title={<DashboardCardTitle icon={<ChatIcon />}>{title}</DashboardCardTitle>}
        />
      </Box>
      
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          paddingX: 2,
          pt: 0,
          mb: 0.5,
        }}
      >
        {positiveCommentGroups.length > 0 && negativeCommentGroups.length > 0 ? (
          <>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Typography variant="subtitle1" sx={{ width: '90%', textAlign: "center", color: '#424242', fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" }, borderBottom: `1px solid ${colors.positiveGreen}50`, paddingBottom: '4px', marginBottom: '4px' }}>
                Positive
              </Typography>
            </Box>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Typography variant="subtitle1" sx={{ width: '90%', textAlign: "center", color: '#424242', fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" }, borderBottom: `1px solid ${colors.negativeRed}50`, paddingBottom: '4px', marginBottom: '4px' }}>
                Negative
              </Typography>
            </Box>
          </>
        ) : positiveCommentGroups.length > 0 ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Typography variant="subtitle1" sx={{ width: '90%', textAlign: "center", color: '#424242', fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" }, borderBottom: `1px solid ${colors.positiveGreen}50`, paddingBottom: '4px', marginBottom: '4px' }}>
              Positive
            </Typography>
          </Box>
        ) : negativeCommentGroups.length > 0 ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Typography variant="subtitle1" sx={{ width: '90%', textAlign: "center", color: '#424242', fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem", lg: "0.95rem" }, borderBottom: `1px solid ${colors.negativeRed}50`, paddingBottom: '4px', marginBottom: '4px' }}>
              Negative
            </Typography>
          </Box>
        ) : null}
      </Box>

      <CardContent
        ref={scrollContainerRef}
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          padding: { xs: 0, md: 0, lg: 0 },
          paddingTop: 0,
          margin: 0,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0, 0, 0, 0.1)', borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
        }}
      >
        {(isLoading && positiveCommentGroups.length === 0 && negativeCommentGroups.length === 0) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}> 
            <CircularProgress />
          </Box>
        ) : noCommentsAtAll ? (
          <Typography variant="caption" color="textSecondary" sx={{textAlign: 'center', display:'block', p:2}}>No insights found for current filters.</Typography>
        ) : (
          positiveCommentGroups.length > 0 && negativeCommentGroups.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "row", px: 1, position: "relative" }}>
              <Divider orientation="vertical" sx={{ position: "absolute", height: "100%", left: "50%", transform: "translateX(-50%)", opacity: 0.1, zIndex: 1 }} />
              <Box sx={{ flex: 1, pr: 0.5 }}>
                <List dense disablePadding>
                  {positiveCommentGroups.map(group => renderCommentGroup(group, 'positive'))}
                  {positiveCommentGroups.length === 0 && !isLoading && (
                    <Typography variant="caption" color="textSecondary" sx={{textAlign: 'center', display:'block', p:2}}>No positive insights.</Typography>
                  )}
                </List>
              </Box>
              <Box sx={{ flex: 1, pl: 0.5 }}>
                <List dense disablePadding>
                  {negativeCommentGroups.map(group => renderCommentGroup(group, 'negative'))}
                  {negativeCommentGroups.length === 0 && !isLoading && (
                    <Typography variant="caption" color="textSecondary" sx={{textAlign: 'center', display:'block', p:2}}>No negative insights.</Typography>
                  )}
                </List>
              </Box>
            </Box>
          ) : positiveCommentGroups.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "row", px: 1 }}>
              <Box sx={{ flex: 1 }}>
                <List dense disablePadding>
                  {positiveCommentGroups.map(group => renderCommentGroup(group, 'positive'))}
                  {positiveCommentGroups.length === 0 && !isLoading && (
                    <Typography variant="caption" color="textSecondary" sx={{textAlign: 'center', display:'block', p:2}}>No positive insights.</Typography>
                  )}
                </List>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "row", px: 1 }}>
              <Box sx={{ flex: 1 }}>
                <List dense disablePadding>
                  {negativeCommentGroups.map(group => renderCommentGroup(group, 'negative'))}
                  {negativeCommentGroups.length === 0 && !isLoading && (
                    <Typography variant="caption" color="textSecondary" sx={{textAlign: 'center', display:'block', p:2}}>No negative insights.</Typography>
                  )}
                </List>
              </Box>
            </Box>
          )
        )}
        {commentsPanelError && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">Error loading comments: {commentsPanelError}</Typography>
          </Box>
        )}
        {!isLoading && commentsPanelHasMore && (
          <Box sx={{ textAlign: 'center', py: 1, height: '40px' }}>
            {isLoading && <CircularProgress size={24} />}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CommentsSideBySide;
